import _ from 'underscore';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';

import {
  stringKeys,
  learnMoreLinkIds,
  panelStateOptions,
  progressStateOptions,
} from '../common/constants';

import './result-table.css';
import './success-statement.css';

export class ResultPanel extends React.PureComponent {
  static propTypes = {
    i18n: PropTypes.shape({
      getString: PropTypes.func.isRequired,
      parseDecimal: PropTypes.func.isRequired,
    }).isRequired,
    panelState: x => _.contains(_.keys(panelStateOptions), x),
    progressState: x => _.contains(_.keys(progressStateOptions), x),
    onApplyChangeButtonClicked: PropTypes.func.isRequired,
    onDownloadResultButtonClicked: PropTypes.func.isRequired,
    onDoneButtonClicked: PropTypes.func.isRequired,
    onCancelButtonClicked: PropTypes.func.isRequired,
  };

  static defaultProps = {
    panelState: panelStateOptions.idle,
    progressState: progressStateOptions.idle,
  };

  constructor(props) {
    super(props);
    // Only for test purpose
    if (this.props.testRef) {
      this.props.testRef.current = {
        that: this,
      };
    }
  }

  state = {
    panelState: this.props.panelState,
    progressState: this.props.progressState,
    fileUpdateRowsCount: 0,
    fileErrorRowsCount: 0,
  };

  setPanelState = (state) => {
    this.setState({
      panelState: state,
    });
  }

  setProgressState = (state) => {
    this.setState({
      progressState: state,
    });
  }

  setResultTableData = (updateCount, errorCount) => {
    this.setState({
      fileUpdateRowsCount: updateCount,
      fileErrorRowsCount: errorCount,
    });
  }

  renderProgressBar = () => (
    <div className="result-panel-progress">
      <div className="progress-status">
        {
          this.props.i18n.getString(this.state.progressState.statementStringKey)
        }
      </div>
      <div className="progress-container" style={{ width: '300px' }}>
        <div
          className="progress-bar"
          style={{ width: this.state.progressState.percentage, height: '10px' }}
        />
      </div>
    </div>
  );

  renderResultTableTitle = (panelStateOption) => {
    switch (panelStateOption) {
      case panelStateOptions.previewResult:
        return this.props.i18n.getString(stringKeys.resultTablePreviewTitle);
      case panelStateOptions.success:
        return this.props.i18n.getString(stringKeys.resultTableSuccessTitle);
      default:
        return null;
    }
  }

  renderResultTable = () => (
    <div className="result-table-container">
      <div className="result-table-title" style={{ marginBottom: '4px' }}>
        {
          this.renderResultTableTitle(this.state.panelState)
        }
      </div>
      <table className="result-table" cellPadding="6" >
        <thead>
          <tr>
            <td>{ this.props.i18n.getString(stringKeys.resultTableHeaderUpdate) }</td>
            <td>{ this.props.i18n.getString(stringKeys.resultTableHeaderError) }</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{ this.state.fileUpdateRowsCount }</td>
            <td>{ this.state.fileErrorRowsCount }</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  renderUserErrorSubpanel = () => (
    <div className="user-error-subpanel">
      <div>Encountered error in creating audience, please try again later.</div>
    </div>
  );

  renderSuccessStatement = () => (
    <div className="result-panel-success-statement">
      <div>
        { this.props.i18n.getString(stringKeys.resultPanelSuccessDescriptionTitle) }
      </div>
      <ul>
        <li>
          { this.props.i18n.getString(stringKeys.resultPanelSuccessDescriptionLine1) }
        </li>
        <li>
          { this.props.i18n.getString(stringKeys.resultPanelSuccessDescriptionLine2) }
        </li>
        <li>
          { this.props.i18n.getString(stringKeys.resultPanelSuccessDescriptionLine3) }
        </li>
        <li className="learn-more">
          <LearnMoreLink
            topic={learnMoreLinkIds.customerList}
            linkText={this.props.i18n.getString(stringKeys.resultPanelSuccessDescriptionLine4)}
          />
        </li>
      </ul>
    </div>
  );

  renderPanelButtons = () => {
    const disabled = this.state.fileUpdateRowsCount <= 0;
    const applyButtonClass = classNames(
      'btn-primary',
      'btn',
      'apply-changes-button',
      { disabled }
    );

    return (
      <div className="result-panel-button-container" style={{ marginTop: '30px' }}>
        {
          this.state.panelState.isApplyChangesButtonVisible ?
            <button
              className={applyButtonClass}
              disabled={disabled}
              onClick={this.props.onApplyChangeButtonClicked}
              style={{ marginRight: 10 }}
            >
              { this.props.i18n.getString(stringKeys.applyChangesButton) }
            </button>
            : null
        }
        {
          this.state.panelState.isDownloadResultButtonVisible ?
            <button className="btn-default btn download-result-button" onClick={this.props.onDownloadResultButtonClicked} style={{ marginRight: 10 }}>
              { this.props.i18n.getString(stringKeys.downloadResultButton) }
            </button>
            : null
        }
        {
          this.state.panelState.isCancelButtonVisible ?
            <button className="btn-default btn cancel-button" style={{ marginRight: 10 }} onClick={this.props.onCancelButtonClicked}>
              { this.props.i18n.getString(stringKeys.cancelButton) }
            </button>
            : null
        }
        {
          this.state.panelState.isDoneButtonVisible ?
            <button className="btn-default btn btn-primary done-button" onClick={this.props.onDoneButtonClicked}>
              { this.props.i18n.getString(stringKeys.doneButton) }
            </button>
            : null
        }
      </div>
    );
  }

  render() {
    if (this.state.panelState === panelStateOptions.idle) {
      return null;
    }

    return (
      <div className="customer-list-result-panel">
        <div className="result-panel-content" style={{ marginLeft: 20 }}>
          {
            this.state.panelState.isProgressBarVisible ? this.renderProgressBar() : null
          }
          {
            this.state.panelState === panelStateOptions.success ?
              this.renderSuccessStatement() : null
          }
          {
            this.state.panelState.isResultTableVisible ? this.renderResultTable() : null
          }
          {
            this.state.panelState.isUserErrorVisible ? this.renderUserErrorSubpanel() : null
          }
        </div>
        {
          this.renderPanelButtons()
        }
      </div>
    );
  }
}
